import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Router, UrlSegment } from '@angular/router';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpService } from './http.service';

const CUSTOM_DOMAIN_ROOT_PATHS = new Set([
  'checkout',
  'review',
  'event-slot',
  'ticket',
  'thank-you',
  'payment',
]);

@Injectable({
  providedIn: 'root'
})
export class RouteHandlerService {
  private document = inject(DOCUMENT);
  private cookie = inject(SsrCookieService);
  private http = inject(HttpService);
  private router = inject(Router);

  get HostName(): string {
    return this.document.location.hostname;
  }

  isTicketsHost(): boolean {
    // tickets.fnkone.com
    return this.HostName === `${environment.subDomain}.${environment.baseDaomain}`;
  }

  isBaseDomainSubdomain(): boolean {
    // does hostname ends with .fnkone.com but not tickets.fnkone.com
    if (this.isTicketsHost()) {
      return false;
    }
    const suffix = `.${environment.baseDaomain}`;
    return this.HostName.endsWith(suffix);
  }

  isExternalCustomDomain(): boolean {
    // other than tickets.fnkone.com and fnkone.com
    return !this.isTicketsHost() && !this.isBaseDomainSubdomain();
  }

  isEventSlugRoutes(): boolean {
    // tickets.fnkone.com or other than fnkone.com and has event slug in path like tickets.nikhil.com/event-slug/event
    return this.isTicketsHost() || (this.isExternalCustomDomain() && this.hasEventSlugInPath());
  }

  isFromDifferentDomain(): boolean {
    // current hostname is does not fnkone.com
    return !this.HostName.endsWith(environment.baseDaomain);
  }

  getSubdomainSlug(): string {
    const suffix = `.${environment.baseDaomain}`;
    if (!this.HostName.endsWith(suffix) || this.isTicketsHost()) {
      return '';
    }
    return this.HostName.slice(0, this.HostName.length - suffix.length);
  }

  private getPathSegments(segments?: UrlSegment[]): string[] {
    if (segments?.length) {
      return segments.map((segment) => segment.path);
    }
    return this.document.location.pathname.split('/').filter(Boolean);
  }

  hasEventSlugInPath(segments?: UrlSegment[]): boolean {
    const parts = this.getPathSegments(segments);
    const first = parts[0];
    if (!first) {
      return false;
    }
    return !CUSTOM_DOMAIN_ROOT_PATHS.has(first);
  }

  getEventSlugFromPath(segments?: UrlSegment[]): string {
    return this.hasEventSlugInPath(segments) ? this.getPathSegments(segments)[0] : '';
  }

  getEventSlugFromUrl(url: string): string {
    const parts = url.split('?')[0].split('/').filter(Boolean);
    const first = parts[0];
    if (!first || CUSTOM_DOMAIN_ROOT_PATHS.has(first)) {
      return '';
    }
    return first;
  }

  getCachedEventSlug(): string {
    return this.cookie.get('eventSlug') || '';
  }

  /** tickets / external slug-path host → route param; custom host → eventSlug cookie */
  resolveEventSlug(routeEventSlug?: string | null): string {
    return this.isEventSlugRoutes()
      ? (routeEventSlug ?? this.getEventSlugFromPath() ?? '')
      : this.getCachedEventSlug();
  }

  cacheSubdomainSlug(): void {
    const slug = this.getSubdomainSlug();
    if (slug) {
      this.cookie.set('eventSlug', slug, { path: '/' });
    }
  }

  routeTo(slug: string, route: string, queryparam?: any): void {
    const parts = route.split('/').filter(Boolean);
    if (!this.isEventSlugRoutes()) {
      if (route === 'event') {
        this.router.navigate(['/']);
      } else {
        this.router.navigate(['/', ...parts], { queryParams: queryparam });
      }
    } else {
      this.router.navigate([slug, ...parts], { queryParams: queryparam });
    }
  }

  fetchAndCacheEventSlug(): Observable<string> {
    const clientDomain = this.HostName;
    const body = { clientDomain };
    return this.http.post(`privateEventApp/v2/get-event-slug/`, body).pipe(
      map((res) => res[0]?.event_slug ?? ''),
      tap((slug) => {
        if (slug) {
          this.cookie.set('eventSlug', slug, { path: '/' });
        }
      })
    );
  }

  verifyEventSlugExists(slug: string): Observable<boolean> {
    const clientDomain = this.HostName;
    const body = { clientDomain };
    return this.http.post(`privateEventApp/v2/get-event-slug/`, body).pipe(
      map((res) => res.some((item:any) => item.event_slug === slug)),
      catchError(() => of(false))
    );
  }
}
